import {
  ITourClientSettings,
} from '@llws/lift-entity-interfaces';
import {
  Equal,
  IQueryCommandOptions,
  convertQueryCommandToQueryString,
} from '@llws/dynamic-router-utils';
import {
  IApiResponse,
  IMetaResponse,
} from '@/shared';
import { StoreBaseImplementationV2 } from '@/shared/classes/store-base-implementation-v2';
import { adminRequest } from '@/shared/request-configs/admin-request';
import vm from '@/main';
import { IAction, IStateStrict } from '@/shared/interfaces/store-base';

interface ITourClientSettingsState {
  list: IApiResponse<ITourClientSettings[], IMetaResponse> | null,
  propertyIdsWithTours: number[],
}

// Base store configuration
const agentGatewayResourcePathAgent: string = '/tour-api-agent';
const adminGatewayResourcePathAdmin: string = '/tour-api-admin';
const listViewName: string = '';
const showLogging: boolean = false;

const defaultGetListFilters: IQueryCommandOptions = {};

const defaultGetSingleFilters: IQueryCommandOptions = {};

// Instantiate the base store with the params
const base = new StoreBaseImplementationV2<ITourClientSettings>({
  adminGatewayResourcePath: adminGatewayResourcePathAdmin,
  defaultGetSingleFilters,
  defaultGetListFilters,
  listViewName,
  showLogging,
});

export default {
  namespaced: base.namespaced,

  state: {
    ...base.state,
    list: [] as IApiResponse<ITourClientSettings[], IMetaResponse> | null,
    propertyIdsWithTours: [] as number[],
  },

  getters: {
    ...base.getters,

    list(state: ITourClientSettingsState): ITourClientSettings[] {
      return state.list?.data || [];
    },
    propertyIdsWithTours(state: ITourClientSettingsState): number[] {
      return state.propertyIdsWithTours || [];
    },

  },

  mutations: {
    ...base.mutations,
    setPropertyIdsWithTours(state: ITourClientSettingsState, data: number[]) {
      state.propertyIdsWithTours = data;
    },
  },

  actions: {
    ...base.actions,
    getTourClientSettings: async (ctx: IAction<IStateStrict<ITourClientSettings>>) => {
      const tourClientSettingsFilters: IQueryCommandOptions = {
        where: [
          Equal('clientId', vm()?.$rsGlobals.$currentClient.id),
        ],
      };

      const response = await adminRequest.get(`${vm()?.$rsGlobals.$hasToursRestricted() ? agentGatewayResourcePathAgent : adminGatewayResourcePathAdmin}/tour_client_settings${convertQueryCommandToQueryString(tourClientSettingsFilters)}`);
      ctx.commit('set', response);
    },
    createTourClientSettings: async (ctx: IAction<IStateStrict<ITourClientSettings>>, clientSettings: Partial<ITourClientSettings>) => {
      const result = await adminRequest.post(`${vm()?.$rsGlobals.$hasToursRestricted() ? agentGatewayResourcePathAgent : adminGatewayResourcePathAdmin}/tour_client_settings`, clientSettings);
      ctx.commit('set', result);
      return result;
    },
    updateTourClientSettings: async (ctx: IAction<IStateStrict<ITourClientSettings>>, clientSettings: Partial<ITourClientSettings>) => {
      const result = await adminRequest.put(`${vm()?.$rsGlobals.$hasToursRestricted() ? agentGatewayResourcePathAgent : adminGatewayResourcePathAdmin}/tour_client_settings/${clientSettings.id}`, clientSettings);
      ctx.commit('set', result);
      return result;
    },
    getPremiumPropertyIdsWithTours: (ctx: IAction<IStateStrict<ITourClientSettings>>) => {
      const assignedPropertyIds = Object.entries(vm()?.$rsGlobals.$currentUser?.products?.building ?? [])
        .filter(([_, products]) => products.tour_admins)
        .map(([key]) => (key));

      // If it's not -1 means user has per property permissions
      if (assignedPropertyIds[0] !== '-1') {
        ctx.commit('setPropertyIdsWithTours', assignedPropertyIds);
      }

      // Return all property IDs with active 'tour_admins' module if user has full permissions
      const response = vm()?.$rsGlobals.$currentUser.getPremiumProductPropertyIds('tour_admins');
      ctx.commit('setPropertyIdsWithTours', response);
    },
  },
};
